import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import CustomToast from "../../components/misc/CustomToast";
import { useIsMobile } from "../../hooks/useIsMobile";
import { cn } from "../../utils/classMerger";
import SocialConnectionMetricCards from "./SocialConnectionMetricCards";
import SocialConnectionsList from "./SocialConnectionsList";
import {
    exportSocialConnectionsCsv,
    getSocialConnectionMetrics,
    getSocialConnections,
} from "./actions";
import {
    PaginatedSocialConnections,
    SocialConnectionMetrics,
    SocialConnectionSortColumn,
    SocialConnectionStatusFilter,
    SortDirection,
} from "./types";

const SocialConnections: React.FC = () => {
    const mobile = useIsMobile(900);

    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [data, setData] = useState<PaginatedSocialConnections>();
    const [metrics, setMetrics] = useState<SocialConnectionMetrics>();
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [platformFilter, setPlatformFilter] = useState("All");
    const [statusFilter, setStatusFilter] =
        useState<SocialConnectionStatusFilter>("all");
    const [sortBy, setSortBy] =
        useState<SocialConnectionSortColumn>("lastDisconnectedAt");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

    // Debounce the search so typing doesn't fire a request per keystroke against the
    // cross-user social-profiles aggregation.
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 350);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const currentFilters = useCallback(
        () => ({
            search: debouncedSearch,
            platform: platformFilter,
            status: statusFilter,
            sortBy,
            sortDirection,
        }),
        [debouncedSearch, platformFilter, statusFilter, sortBy, sortDirection]
    );

    const fetchConnections = useCallback(
        async (p: number = page) => {
            setLoading(true);
            try {
                const result = await getSocialConnections({
                    page: p,
                    limit,
                    ...currentFilters(),
                });
                setData(result);
            } catch {
                toast.error(
                    <CustomToast
                        message="Failed to load social connections"
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
            } finally {
                setLoading(false);
            }
        },
        [page, limit, currentFilters]
    );

    useEffect(() => {
        fetchConnections(page);
    }, [page, fetchConnections]);

    useEffect(() => {
        const loadMetrics = async (): Promise<void> => {
            try {
                setMetrics(await getSocialConnectionMetrics());
            } catch {
                toast.error(
                    <CustomToast
                        message="Failed to load social connection metrics"
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
            }
        };
        loadMetrics();
    }, []);

    const handleSortChange = (
        column: SocialConnectionSortColumn,
        direction: SortDirection
    ): void => {
        setSortBy(column);
        setSortDirection(direction);
    };

    const handleExport = async (): Promise<void> => {
        setExporting(true);
        try {
            await exportSocialConnectionsCsv(currentFilters());
            toast.success(
                <CustomToast message="Export downloaded" type="success" />,
                { autoClose: 2000 }
            );
        } catch {
            toast.error(
                <CustomToast
                    message="Failed to export social connections"
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className={mobile ? "p-4" : "p-6"}>
            <div className={cn("grid items-start gap-4")}>
                <div className="grid gap-1 px-2">
                    <h1
                        className={cn(
                            "font-bold tracking-tight text-primary",
                            mobile ? "text-2xl" : "text-3xl"
                        )}
                    >
                        Social Connections
                    </h1>
                    <p className="text-gray-500">
                        What every user has connected in Ayrshare, what has
                        disconnected, and which posts are stranded on Social
                        Pause.
                    </p>
                </div>

                {metrics && (
                    <SocialConnectionMetricCards
                        metrics={metrics}
                        isMobile={mobile}
                    />
                )}

                <SocialConnectionsList
                    rows={data?.data || []}
                    loading={loading}
                    exporting={exporting}
                    page={page}
                    setPage={setPage}
                    limit={limit}
                    total={data?.total || 0}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    platformFilter={platformFilter}
                    setPlatformFilter={setPlatformFilter}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSortChange={handleSortChange}
                    onExport={handleExport}
                    isMobile={mobile}
                />
            </div>
        </div>
    );
};

export default SocialConnections;
